import React, { useCallback } from 'react';
import { useNavigation } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import type { ViewProps } from 'react-native';
import { Platform, StyleSheet, View } from 'react-native';

import { Button, HStack, IllusEmployeeBag, Image, Modal, theme, useResponsive } from '@sg/garnish';

import { useHeaderButtonOptions } from '../../../../components/Header';
import { TransparentModal } from '../../../../components/TransparentModal';
import {
  useCancelReorder,
  useConfirmLocation,
  useDismissInactiveReorderRoute,
  useFinishReorder,
  useReorderState,
} from '../../hooks';
import { useReorderTelemetry } from '../../machines/Reorder.telemetry';
import { useReorderRightIcon } from '../ReorderConfirmLocation/ReorderConfirmLocation.components';

const illustration = IllusEmployeeBag;

export const ReorderActiveBagWarning = () => {
  const { previousEvent, currentStep } = useReorderState();
  const confirmLocation = useConfirmLocation();
  const cancelReorder = useCancelReorder();
  const finishReorder = useFinishReorder();
  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { newOrderTelemetry, returnToBagTelemetry } = useReorderTelemetry();

  useDismissInactiveReorderRoute();

  const onNewOrder = useCallback(() => {
    newOrderTelemetry();
    confirmLocation(previousEvent);
  }, [newOrderTelemetry, confirmLocation, previousEvent]);

  const onReturnToBag = useCallback(() => {
    returnToBagTelemetry();
    finishReorder();
  }, [returnToBagTelemetry, finishReorder]);

  if (currentStep !== 'activeBagWarning' && Platform.OS === 'web') return null;

  const CurrentLayout = currentBreakpoint.isXS ? MobileLayout : DesktopLayout;

  return (
    <TransparentModal
      testID="reorder-active-bag-warning"
      accessibilityLabel={formatMessage(messages.reorderActiveBagWarningHeadline)}
      size="medium"
      onClose={cancelReorder}
      mobileEdges={['bottom']}
    >
      <CurrentLayout newOrder={onNewOrder} returnToBag={onReturnToBag}>
        <Modal.Headline>{formatMessage(messages.reorderActiveBagWarningHeadline)}</Modal.Headline>

        <Modal.BodyText>{formatMessage(messages.reorderActiveBagWarningMessage)}</Modal.BodyText>
      </CurrentLayout>
    </TransparentModal>
  );
};

const DesktopLayout = ({
  children,
  returnToBag,
  newOrder,
}: Readonly<{
  children: ViewProps['children'];
  returnToBag: () => void;
  newOrder: () => void;
}>) => {
  const { formatMessage } = useIntl();

  return (
    <Layout returnToBag={returnToBag} newOrder={newOrder}>
      <View style={styles.desktopImageContainer}>
        <Image style={styles.image} source={illustration} />
      </View>
      <View style={styles.content}>
        <Modal.CloseBtn
          onPress={returnToBag}
          accessibilityHint={formatMessage(messages.reorderActiveBagWarningCloseA11yHint)}
          accessibilityLabel={formatMessage(messages.reorderActiveBagWarningClose)}
          testID="reorder.close-button"
        />
        {children}
      </View>
    </Layout>
  );
};

const MobileLayout = ({
  children,
  returnToBag,
  newOrder,
}: Readonly<{
  children: ViewProps['children'];
  returnToBag: () => void;
  newOrder: () => void;
}>) => {
  const rightIcon = useReorderRightIcon();
  const navigation = useNavigation();
  const renderHeaderRight = useCallback(() => rightIcon, [rightIcon]);
  const { headerRightOptions } = useHeaderButtonOptions({ headerRight: renderHeaderRight });

  React.useLayoutEffect(() => {
    navigation.setOptions({
      ...headerRightOptions,
      headerShown: true,
    });
  }, [headerRightOptions, navigation]);

  return (
    <Layout returnToBag={returnToBag} newOrder={newOrder}>
      <View style={styles.mobileImageContainer}>
        <Image style={styles.image} source={illustration} />
      </View>
      <View style={styles.content}>{children}</View>
    </Layout>
  );
};

const Layout = ({
  children,
  returnToBag,
  newOrder,
}: Readonly<{
  children: ViewProps['children'];
  returnToBag: () => void;
  newOrder: () => void;
}>) => {
  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  return (
    <>
      <View style={match([styles.column, styles.row])}>{children}</View>

      <Modal.Footer style={match([undefined, styles.desktopFooter])} withoutTopBorder>
        <HStack itemsPerRow={2} gap={theme.spacing['4']}>
          <Button
            size="large"
            palette="secondary"
            onPress={returnToBag}
            accessibilityLabel={formatMessage(messages.reorderActiveBagWarningReturnToBag)}
            accessibilityHint={formatMessage(messages.reorderActiveBagWarningReturnToBag)}
            accessibilityRole="button"
            testID="reorder.active-bag-warning.return-to-bag"
          >
            {formatMessage(messages.reorderActiveBagWarningReturnToBag)}
          </Button>
          <Button
            size="large"
            onPress={newOrder}
            accessibilityLabel={formatMessage(messages.reorderActiveBagWarningNewOrder)}
            accessibilityHint={formatMessage(messages.reorderActiveBagWarningNewOrder)}
            accessibilityRole="button"
            testID="reorder.active-bag-warning.new-order"
          >
            {formatMessage(messages.reorderActiveBagWarningNewOrder)}
          </Button>
        </HStack>
      </Modal.Footer>
    </>
  );
};

const styles = StyleSheet.create({
  content: {
    flex: 1,
  },
  desktopFooter: {
    paddingTop: theme.spacing['6'],
  },
  mobileImageContainer: {
    minHeight: 212,
    paddingVertical: theme.spacing['6'],
    marginVertical: theme.spacing['6'],
    alignItems: 'center',
  },
  desktopImageContainer: {
    paddingTop: theme.spacing['6'],
    paddingLeft: theme.spacing['6'],
  },
  image: {
    height: 212,
    width: 212,
  },
  row: {
    flexDirection: 'row',
  },
  column: {
    flex: 1,
    flexDirection: 'column',
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  reorderActiveBagWarningHeadline: {
    defaultMessage: 'You already have an active order',
    description: 'Reorder | Active Bag Warning | Headline',
  },
  reorderActiveBagWarningMessage: {
    defaultMessage: 'Would you like to continue with a new order or return to your current bag?',
    description: 'Reorder | Active Bag Warning | Message',
  },
  reorderActiveBagWarningCloseA11yHint: {
    defaultMessage: 'Closes the modal',
    description: 'Reorder Active Bag Warning | Close | A11y Hint',
  },
  reorderActiveBagWarningClose: {
    defaultMessage: 'Close',
    description: 'Reorder Active Bag Warning | Close',
  },
  reorderActiveBagWarningReturnToBag: {
    defaultMessage: 'Return to bag',
    description: 'Reorder | Active Bag Warning | Return To Bag',
  },
  reorderActiveBagWarningNewOrder: {
    defaultMessage: 'New order',
    description: 'Reorder | Active Bag Warning | New Order',
  },
});
